(() => {
  const seen = new WeakSet();
  const active = new Map();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      preview(entry.target);
    });
  }, {rootMargin: '120px'}) : null;

  function sourceFor(image) {
    const picture = image.parentElement?.tagName === 'PICTURE' ? image.parentElement : null;
    const source = picture && [...picture.querySelectorAll('source[srcset]')].find(node => !node.media || matchMedia(node.media).matches);
    const value = source?.srcset.split(',')[0].trim().split(/\s+/)[0] || image.currentSrc || image.src;
    try {
      const url = new URL(value, location.href);
      if (!['http:', 'https:'].includes(url.protocol) || !/\.(?:jpe?g|png|webp|avif)$/i.test(url.pathname)) return null;
      if (!url.pathname.includes('/cdn/shop/') && !(url.hostname === 'cdn.shopify.com' && url.pathname.includes('/s/files/'))) return null;
      url.searchParams.set('width', '160');
      return url.href;
    } catch { return null; }
  }

  function preview(image) {
    if (!image.isConnected || (image.complete && image.naturalWidth)) return;
    const rect = image.getBoundingClientRect();
    if (Math.max(rect.width, rect.height) < 240 || !rect.width || !rect.height) return;
    if (!observer && (rect.bottom < -120 || rect.top > innerHeight + 120)) return;
    const url = sourceFor(image);
    if (!url) return;
    const style = getComputedStyle(image);
    if (style.backgroundImage !== 'none') return;
    const fit = style.objectFit;
    const position = style.objectPosition;
    const placeholder = new Image();
    let finished = false;
    const clear = () => {
      if (finished) return;
      finished = true;
      active.delete(image);
      image.classList.remove('tm-image-preview');
      image.style.removeProperty('--tm-image-preview');
      image.style.removeProperty('--tm-image-fit');
      image.style.removeProperty('--tm-image-position');
      image.removeEventListener('load', loaded);
    };
    const loaded = async () => {
      if (!image.naturalWidth) return;
      try { await image.decode(); } catch {}
      if (image.complete && image.naturalWidth) clear();
    };
    image.addEventListener('load', loaded);
    active.set(image, () => { clear(); preview(image); });
    placeholder.onload = () => {
      if (finished || !image.isConnected || (image.complete && image.naturalWidth)) { clear(); return; }
      if (sourceFor(image) !== url) { clear(); preview(image); return; }
      image.style.setProperty('--tm-image-preview', `url("${url}")`);
      image.style.setProperty('--tm-image-fit', fit === 'cover' ? 'cover' : fit === 'fill' ? '100% 100%' : 'contain');
      image.style.setProperty('--tm-image-position', position);
      image.classList.add('tm-image-preview');
    };
    placeholder.onerror = clear;
    placeholder.fetchPriority = rect.top < innerHeight && rect.bottom > 0 ? 'high' : 'low';
    placeholder.decoding = 'async';
    placeholder.src = url;
  }

  function scan(root) {
    const images = root.matches?.('img') ? [root] : [...root.querySelectorAll('img')];
    images.forEach(image => {
      if (seen.has(image) || (image.complete && image.naturalWidth)) return;
      if (image.closest('.engineering-media, .hero-ambience, .product-motion, [data-motion-static-figure]')) return;
      if (!sourceFor(image)) return;
      seen.add(image);
      if (observer) observer.observe(image); else preview(image);
    });
  }

  scan(document);
  let resizeFrame = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => [...active.values()].forEach(restart => restart()));
  }, {passive: true});
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
    if (node.nodeType === 1) scan(node);
  }))).observe(document.body, {childList: true, subtree: true});
})();
